import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { driverApi } from '../services/driverApi';
import { VehicleNote, AddVehicleNoteDTO, GetVehicleNotesResponseDTO } from '../types';

// Get vehicle notes
export const useGetVehicleNotes = (vehicleId: string | undefined) => {
  return useQuery({
    queryKey: ['vehicleNotes', vehicleId],
    queryFn: () => driverApi.getVehicleNotes(vehicleId!),
    enabled: !!vehicleId,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 2 * 60 * 1000, // Refetch every 2 minutes
  });
};

// Add vehicle note
export const useAddVehicleNote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ vehicleId, noteData }: { vehicleId: string; noteData: AddVehicleNoteDTO }) =>
      driverApi.addVehicleNote(vehicleId, noteData),
    onSuccess: (_, { vehicleId }) => {
      // Invalidate and refetch vehicle notes for this vehicle
      queryClient.invalidateQueries({ queryKey: ['vehicleNotes', vehicleId] });
    },
  });
};

// Update vehicle note
export const useUpdateVehicleNote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ noteId, noteData }: { noteId: string; noteData: AddVehicleNoteDTO }) =>
      driverApi.updateVehicleNote(noteId, noteData),
    onSuccess: (data) => {
      // Invalidate vehicle notes for the vehicle this note belongs to
      if (data?.vehicleId) {
        queryClient.invalidateQueries({ queryKey: ['vehicleNotes', data.vehicleId] });
      } else {
        // If we don't have vehicleId in response, invalidate all vehicle notes
        queryClient.invalidateQueries({ queryKey: ['vehicleNotes'] });
      }
    },
  });
};

// Delete vehicle note
export const useDeleteVehicleNote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (noteId: string) => driverApi.deleteVehicleNote(noteId),
    onSuccess: (_, noteId) => {
      // Invalidate all vehicle notes since we don't know which vehicle this note belonged to
      queryClient.invalidateQueries({ queryKey: ['vehicleNotes'] });
    },
  });
}; 